import { Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef, ReactElement, useMemo } from 'react';
import { Accordion } from 'semantic-ui-react';
import { AccordionWidgetData } from '../../../models/RiskProducts';
import { DATE_FORMATS, formatDate } from '../../../utils/dateUtils';
import ExpandableMetric from '../common/ExpandableMetric';
import styles from '../AcordionList.module.scss';
import { getRiskOverviewData } from './RiskOverviewWidget.logic';
import { CALCULATION_DATE, FUNDAMENTAL_DATE, MODEL, NOTES } from './RiskOverviewWidget.constants';
import { RiskOverviewProps } from './RiskOverviewWidget.types';

const cx = classNames.bind(styles);

const RiskOverviewWidget = (
  { riskOverviewMetrics, print = false, ...sortableWidgetProps }: RiskOverviewProps,
  ref
): ReactElement | null => {
  const riskOverviewData = useMemo(() => {
    return getRiskOverviewData(riskOverviewMetrics);
  }, [riskOverviewMetrics]);

  const areChildrenEmpty = (metric: AccordionWidgetData): boolean => {
    return metric.children.every((item) => item.value == null);
  };

  const renderOverview = () => {
    return riskOverviewData.map((metric) => {
      return (
        <ExpandableMetric
          key={`overview-${metric.item.label}`}
          isItemExpanded={!areChildrenEmpty(metric)}
          metric={metric}
          print={print}
        />
      );
    });
  };

  const renderModelContent = () => {
    return (
      <div className={cx('notes')}>
        <div className={cx('text')}>
          {CALCULATION_DATE}
          <span className={cx('info')}>
            {formatDate(riskOverviewMetrics?.calc_date, DATE_FORMATS.MONTH_ABBR_DATE, '-')}
          </span>
        </div>
        <div className={cx('text')}>
          {FUNDAMENTAL_DATE}
          <span className={cx('info')}>
            {formatDate(riskOverviewMetrics?.fund_date, DATE_FORMATS.MONTH_ABBR_DATE, '-')}
          </span>
        </div>
        <div className={cx('text')}>
          {MODEL}
          <span className={cx('info')}>
            {riskOverviewMetrics?.model_details ? riskOverviewMetrics.model_details : '-'}
          </span>
        </div>
      </div>
    );
  };

  const renderNotes = () => {
    return (
      <div className={cx('notes')}>
        <div className={cx('title')}>{NOTES}</div>
        <div className={cx('text')}>{riskOverviewMetrics?.notes ? riskOverviewMetrics.notes : '-'}</div>
      </div>
    );
  };

  return (
    <Widget headerTitle="Risk Overview" dataTestId="risk-overview-widget" ref={ref} {...sortableWidgetProps}>
      <div className={cx('body')} data-testid="risk-overview-content">
        <div className={cx('containers')}>
          <Accordion exclusive={false}>{renderOverview()}</Accordion>
          <div className={cx('notes-container')}>
            {renderModelContent()}
            {renderNotes()}
          </div>
        </div>
      </div>
    </Widget>
  );
};

export default forwardRef(RiskOverviewWidget);
